import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FaUserCircle, FaStar } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import AppConfig from '../config/appConfig'; // path আপনার ফোল্ডার স্ট্রাকচার অনুযায়ী ঠিক করুন

function ProfilePage() {
  const navigate = useNavigate();
  const { isAuthenticated, userInfo, fetchUserInfo, getAuthHeaders } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    phone_number: '',
    picture: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (userInfo) {
      setFormData({
        name: userInfo.name || '',
        email: userInfo.email || '',
        address: userInfo.address || '',
        phone_number: userInfo.phone_number || '',
        picture: userInfo.picture || '',
        password: ''
      });
    } else {
      fetchUserInfo();
    }
  }, [isAuthenticated, userInfo, navigate, fetchUserInfo]);

  const profileCompletionPercentage = userInfo?.profile_completion || 0;
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (profileCompletionPercentage / 100) * circumference;

  const getMembershipLevel = (points) => {
    if (points >= 1000) return 'Diamond';
    if (points >= 700) return 'Platinum';
    if (points >= 400) return 'Gold';
    if (points >= 100) return 'Silver';
    return 'Bronze';
  };

  const currentPoints = userInfo?.points || 0;
  const currentLevel = getMembershipLevel(currentPoints);

  const levelThresholds = {
    'Bronze': 0,
    'Silver': 100,
    'Gold': 400,
    'Platinum': 700,
    'Diamond': 1000,
  };

  const calculateProgressForBar = (points) => {
    const maxPointsForBar = 1000;
    return Math.min(100, (points / maxPointsForBar) * 100);
  };

  const progressPercentage = calculateProgressForBar(currentPoints);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setSuccessMessage('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        address: formData.address,
      };

      // Only include password if user entered one
      if (formData.password && formData.password.trim() !== '') {
        updateData.password = formData.password;
      }

      const response = await fetch(`${AppConfig.API_BASE_URL}/update_user_profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('Profile updated successfully!');
        await fetchUserInfo();
        // Clear password field after successful update
        setFormData(prev => ({ ...prev, password: '' }));
        window.location.reload();
      } else {
        setError(data.error || 'Problem updating profile.');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Network problem or server error.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8" style={{ fontFamily: 'Assistant, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div className="max-w-7xl mx-auto px-3 md:px-6">
        {/* Page Title */}
        <h1 className="text-lg md:text-3xl font-bold text-gray-900 mb-3 md:mb-6 pb-2 md:pb-3 border-b border-gray-300">
          My Profile
        </h1>

        {/* Two Column Layout for Desktop, Single Column for Mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-6">
          {/* Left Column - Profile Info & Points */}
          <div className="lg:col-span-1 space-y-3 md:space-y-6">
            {/* Profile Card */}
            <div className="bg-white border border-gray-300 rounded-lg p-3 md:p-6">
              <div className="flex flex-col items-center">
                {/* Avatar */}
                <div className="mb-2 md:mb-4">
                  {formData.picture ? (
                    <img
                      src={formData.picture}
                      alt="User Avatar"
                      className="w-16 h-16 md:w-24 md:h-24 rounded-full object-cover border-4 border-orange-500"
                    />
                  ) : (
                    <FaUserCircle className="w-16 h-16 md:w-24 md:h-24 text-gray-300 border-4 border-orange-500 rounded-full p-1" />
                  )}
                </div>

                {/* Name */}
                <h2 className="text-sm md:text-xl font-bold text-gray-900 mb-2 md:mb-4 text-center">
                  {formData.name || 'Guest User'}
                </h2>

                {/* Profile Completion Circle */}
                <div className="relative w-20 h-20 md:w-28 md:h-28">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                    <circle
                      cx="60"
                      cy="60"
                      r={radius}
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="10"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r={radius}
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="10"
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={offset}
                      className="transition-all duration-500"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-base md:text-2xl font-bold text-gray-900">
                      {profileCompletionPercentage}%
                    </span>
                  </div>
                </div>
                <p className="text-[10px] md:text-sm text-gray-600 mt-1 md:mt-2">Profile Completion</p>
              </div>
            </div>

            {/* Points and Membership Card */}
            <div className="bg-white border border-gray-300 rounded-lg p-3 md:p-6">
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-2 md:mb-4 pb-2 border-b border-gray-200">
                Your Points & Level
              </h3>

              {/* Current Points */}
              <div className="text-center mb-2 md:mb-4">
                <div className="flex items-center justify-center gap-1 md:gap-2 mb-1 md:mb-2">
                  <FaStar className="text-yellow-500 text-base md:text-2xl" />
                  <span className="text-xl md:text-4xl font-bold text-blue-600">
                    {currentPoints}
                  </span>
                </div>
                <p className="text-[10px] md:text-sm text-gray-600">Points</p>
              </div>

              {/* Membership Level */}
              <div className="text-center mb-3 md:mb-6">
                <p className="text-[11px] md:text-base font-semibold text-gray-700 mb-1">
                  Current Level
                </p>
                <p className="text-base md:text-xl font-bold text-orange-600 uppercase tracking-wide">
                  {currentLevel}
                </p>
              </div>

              {/* Progress Bar */}
              <div className="mb-2 md:mb-4">
                <div className="w-full bg-gray-200 rounded-full h-2 md:h-4 overflow-hidden">
                  <div
                    className="bg-green-500 h-full rounded-full transition-all duration-500"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>

              {/* Level Labels */}
              <div className="flex justify-between text-[8px] md:text-xs font-semibold mb-3 md:mb-6">
                {Object.keys(levelThresholds).map((level) => (
                  <div
                    key={level}
                    className={`text-center flex-1 ${currentPoints >= levelThresholds[level]
                      ? 'text-orange-600'
                      : 'text-gray-400'
                      }`}
                  >
                    <div>{levelThresholds[level]}</div>
                    <div className="mt-0.5 md:mt-1">{level}</div>
                  </div>
                ))}
              </div>

              {/* Points Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 md:p-4">
                <p className="text-[9px] md:text-sm text-gray-700 leading-relaxed">
                  You can earn points by **purchasing products** or **completing other tasks**. Use these points to **get discounts** or **enjoy special benefits**! Earn more points and upgrade your level!
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Edit Profile Form */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-300 rounded-lg p-3 md:p-6">
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-3 md:mb-6 pb-2 border-b border-gray-200">
                Edit Profile
              </h3>

              <form onSubmit={handleSubmit} className="space-y-3 md:space-y-5">
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="block text-[11px] md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="w-full px-3 py-2 md:px-4 md:py-3 text-xs md:text-base border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-[11px] md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    className="w-full px-3 py-2 md:px-4 md:py-3 text-xs md:text-base border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Address Field */}
                <div>
                  <label htmlFor="address" className="block text-[11px] md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter your address"
                    className="w-full px-3 py-2 md:px-4 md:py-3 text-xs md:text-base border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Phone Number Field (Disabled) */}
                <div>
                  <label htmlFor="phone_number" className="block text-[11px] md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                    Mobile Number
                  </label>
                  <input
                    type="text"
                    id="phone_number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    disabled
                    placeholder="Your mobile number"
                    className="w-full px-3 py-2 md:px-4 md:py-3 text-xs md:text-base border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                  />
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1">Phone number cannot be changed</p>
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-[11px] md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password || ''}
                    onChange={handleInputChange}
                    placeholder="Enter new password (leave blank to keep current)"
                    className="w-full px-3 py-2 md:px-4 md:py-3 text-xs md:text-base border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  />
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1">Leave blank if you don't want to change password</p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded p-2 md:p-4">
                    <p className="text-[11px] md:text-sm text-red-600 font-semibold">{error}</p>
                  </div>
                )}

                {/* Success Message */}
                {successMessage && (
                  <div className="bg-green-50 border border-green-200 rounded p-2 md:p-4">
                    <p className="text-[11px] md:text-sm text-green-600 font-semibold">{successMessage}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-2 md:py-3 text-xs md:text-base font-bold rounded transition-all ${loading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-orange-500 text-white hover:bg-orange-600 active:bg-orange-700'
                    }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-3 w-3 md:h-5 md:w-5 mr-2 md:mr-3 border-2 border-white border-t-transparent rounded-full" />
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
